/**
 * AgenticStreamProvider — mount point cho user-scoped SSE connection.
 *
 * Render 1 lần ở app root (sau auth). Giữ 1 fetch() connection đến
 * /api/chat/stream và fan-out events vào agenticStore theo session_id.
 *
 * ```tsx
 * // app/layout.tsx hoặc sau AuthGuard
 * <AgenticStreamProvider enabled={isAuthenticated}>
 *   {children}
 * </AgenticStreamProvider>
 * ```
 */

import React, { useEffect } from "react";
import { useAgenticStream } from "@/shared/hooks/useAgenticStream";
import { useAgenticStore } from "@/shared/stores/agenticStore";

interface AgenticStreamProviderProps {
  children: React.ReactNode;
  enabled?: boolean;
}

export function AgenticStreamProvider({
  children,
  enabled = true,
}: AgenticStreamProviderProps) {
  const handleEvent = useAgenticStore((s) => s.handleEvent);
  const pruneOldTasks = useAgenticStore((s) => s.pruneOldTasks);

  // Mount user-scoped SSE connection
  useAgenticStream({
    enabled,
    onEvent: handleEvent,
    onConnected: () => {
      // Re-subscribe là safe — store state được giữ nguyên qua reconnect
      console.debug("[AgenticStream] connected");
    },
  });

  // Prune completed tasks mỗi 10 phút để tránh memory leak
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => pruneOldTasks(), 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [enabled, pruneOldTasks]);

  return <>{children}</>;
}
